import axios from 'axios';

export const getUsers = async () => {
  // Use the configured instance (has token + baseURL) instead of bare axios
  const { data } = await api.get('/account/holders');
  return data;
};
import { jwtDecode } from 'jwt-decode';  // FIXED: Named import (no default in v4+)

const API_BASE = process.env.NODE_ENV === 'production' 
  ? `${window.location.origin}/api` 
  : '/api';  // Works on both dkdance.studio and kdance.studio domains

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor: Add JWT if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: Handle 401 (logout/redirect), errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      window.location.href = '/login';  // Berry's login route
    }
    return Promise.reject(error);
  }
);

// Helper: Check if token is expired
export const isTokenExpired = () => {
  const token = localStorage.getItem('accessToken');
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// Auth helpers
export const login = async (credentials) => {
  const { data } = await api.post('/account/login', credentials);
  if (data.status === 'success') {
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('user', JSON.stringify(data.user));
  }
  return data;
};

export const logout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

export const getProfile = async () => {
  const { data } = await api.get('/account/profile');
  return data;
};

// Spaces (NEW: Full CRUD)
export const getSpaces = async () => {
  const { data } = await api.get('/spaces');
  return data.spaces || [];
};

export const createSpace = async (spaceData) => {
  const { data } = await api.post('/spaces', spaceData);
  return data.space;
};

export const updateSpace = async (id, updates) => {  // NEW: For edit in Spaces page
  const { data } = await api.put(`/spaces/${id}`, updates);
  return data.space;
};

export const deleteSpace = async (id) => {  // NEW: For delete in Spaces page
  const { data } = await api.delete(`/spaces/${id}`);
  return data;
};

// Bookings (with optional filters)
export const getBookings = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  console.log('getBookings params:', params);  // DEBUG: Log request params
  try {
    const { data } = await api.get(`/bookings?${params}`);
    console.log('getBookings response:', data);  // DEBUG: Log full response
    return data.bookings || [];
  } catch (err) {
    console.error('getBookings error:', err.response?.data || err.message, err.response?.status);  // DEBUG: Log error details
    throw err;  // Re-throw for component handling
  }
};

export const createBooking = async (bookingData) => {
  console.log('createBooking payload:', bookingData);  // DEBUG: Log payload
  try {
    const { data } = await api.post('/bookings', bookingData);
    console.log('createBooking response:', data);  // DEBUG: Log response
    return data.bookings || [];
  } catch (err) {
    console.error('createBooking error:', err.response?.data || err.message, err.response?.status);  // DEBUG: Log error details
    throw err;
  }
};

export const updateBooking = async (id, updates) => {
  const { data } = await api.put(`/bookings/${id}`, updates);
  return data.booking;
};

export const deleteBooking = async (id) => {
  const { data } = await api.delete(`/bookings/${id}`);
  return data;
};

export const getRules = async () => {
  const { data } = await api.get('/rules');
  return data.rules || [];
};

export const createRule = async (ruleData) => {
  const { data } = await api.post('/rules', ruleData);
  return data.rule;
};

export const updateRule = async (id, updates) => {
  const { data } = await api.put(`/rules/${id}`, updates);
  return data.rule;
};

export const deleteRule = async (id) => {
  const { data } = await api.delete(`/rules/${id}`);
  return data;
};

// Integrations (e.g., status)
export const getStatus = async () => {
  const { data } = await api.get('/integrations/status');
  return data;
};

// Waitlist functions
export const getWaitlistCount = async (slotStart, slotEnd) => {
  const { data } = await api.get(`/bookings/waitlist/count?slot_start=${slotStart}&slot_end=${slotEnd}`);
  return data.count || 0;
};

export const joinWaitlist = async (payload) => {
  const { data } = await api.post('/bookings/waitlist', payload);
  return data;
};

export const removeFromWaitlist = async (payload) => {
  const { data } = await api.post('/bookings/waitlist-remove', payload);
  return data;
};

export default api;
